import OnPlatePosition from "../components/plate/OnPlatePosition.js";
import {SelectionHelper} from "../components/visualization/VisualizationDataSelection.js";

export default class PlateView {
    /** @type {Map<string, {injection: InjectionJson, color: string}>} */
    #comparedInjectionIds = new Map();
    #callbacks = {
        onCellClick: [],
        onChangeSelection: [],
        onInjectionHover: [],
    }
    /**
     * @param {Node} parentNode
     * @param {PlateMeta} plateMeta
     * @param opts
     */
    constructor(parentNode, plateMeta, opts = {}) {
        this._plateMeta = plateMeta;
        const {root, cellSize, cells} = this._drawEmptyPlate(parentNode, plateMeta);
        this._root = root;
        this._d3cells = cells;
        this._cellSize = cellSize;
        /** @type {Plate} */
        this._plate = null;
        cells.all().call(this._onDrag(opts.isInjectionPage))
        this._root.addEventListener("mousedown", (e) => {
            // Because when we select injections for comparing, we don't want to select text on the page
            if(e.shiftKey){
                e.preventDefault()
            }
        })
    }

    /** @param {Plate} plate */
    drawPlate(plate) {
        this._plate = plate;
        this._d3cells.all().each((pos, i, group) => {
            const d3Cell = d3.select(group[i]);
            const injection = plate.getInjection(pos);
            d3Cell.classed('not-empty', !!injection);
            const isCompared = injection && this.#comparedInjectionIds.has(injection.id);
            d3Cell.style("fill", isCompared ? this.#comparedInjectionIds.get(injection.id).color : "");
            d3Cell.classed("compared", isCompared);
            if (isCompared)
                d3Cell.classed("user-selection", false)
            d3Cell.attr('data-injection-id', injection ? injection.id : null);
        });
    }

    showProgressBar() {
        this._root.querySelector('.progress').classList.toggle('visually-hidden', false);
    }

    hideProgressBar() {
        this._root.querySelector('.progress').classList.toggle('visually-hidden', true);
    }

    onCellClick(callback) {
        this.#callbacks.onCellClick.push(callback);
    }

    onChangeSelection(callback) {
        this.#callbacks.onChangeSelection.push(callback);
    }

    onInjectionHover(callback) {
        this.#callbacks.onInjectionHover.push(callback);
    }

    /**
     * @param levels
     * @return {Paginator}
     */
    addPaginator(levels) {
        this._root.insertAdjacentHTML('beforeend', `<paginator-cmp totalPages="${levels}"></paginator-cmp>`);
        return this._root.lastElementChild;
    }

    /** @param {OnPlatePosition | null} pos */
    setActiveCell(pos) {
        this._d3cells.all()
            .classed('active', cellCoords => pos && cellCoords.rowIndex === pos.rowIndex && cellCoords.colIndex === pos.colIndex)
    }

    /** @param {Map<string, {injection: InjectionJson, color: string}>} selectedInjections */
    toggleComparingHighlight(selectedInjections) {
        this.#comparedInjectionIds = selectedInjections;
        const allCells = this._d3cells.all();
        allCells.each(function() {
            const cell = d3.select(this);
            cell.classed('compared', false);
            if (cell.node().style.fill) {
                cell.style("fill", null);
            }
        });

        for (const [key, value] of selectedInjections) {
            let pos = this._plate.getCoordinatesByInjectionId(key);
            if (!pos)
                continue
            const cell = this._d3cells.get(pos);
            if (cell) {
                cell.style("fill", value.color);
                cell.classed("compared", true);
                cell.classed('user-selection', false);
            }
        }
    }

    /** @param {OnPlatePosition} pos */
    addMultilevelMark(pos){
        const {width} = this._cellSize;
        const markSize = width / 2;
        d3.select(this._d3cells.get(pos).node().parentNode)
            .append('polygon')
            .attr('class', 'multi-injection')
            .attr('points', `${markSize},0 ${width},0 ${width},${markSize}`);
    }

    remove(){
        this._root.remove();
    }

    /**
     *
     * @param {Node} parentNode
     * @param {PlateMeta} plateMeta
     * @return {{cells: {all: function(): d3.selection, get: function(OnPlatePosition): d3.selection},
     *           root: Element,
     *           cellSize: {width, height}}}
     * @private
     */
    _drawEmptyPlate(parentNode, plateMeta) {
        /* Append plate container with empty SVG to the parent node */
        let template = document.createElement('template');
        template.innerHTML = PlateView.htmlTemplate(plateMeta);
        parentNode.appendChild(template.content.cloneNode(true));

        let root = parentNode.lastChild;
        const svg = root.querySelector('svg');

        const {cols, rows} = plateMeta;
        const cellSize = {
            width: (svg.clientWidth || 238) / cols - 2,
            height: (svg.clientHeight || 176) / rows - 2
        }
        /* Create matrix of cells to have coordinates on the plate */
        const plateCells = [];
        for (let i = 0; i < rows; i++) {
            const row = [];
            for (let j = 0; j < cols; j++) {
                row.push(new OnPlatePosition(i, j));
            }
            plateCells.push(row);
        }
        /* Append group for each row */
        const rowGroups = d3.select(svg).selectAll('g')
            .data(plateCells).enter()
            .append('g')
            .attr("transform", (row, i) => `translate(0,${i * (cellSize.height + 2)})`);

        /* Append group for each cell */
        const cellGroups = rowGroups.selectAll('g')
            .data(d => d)
            .enter()
            .append('g')
            .attr('transform', (coords, i) => `translate(${i * (cellSize.width + 2)}, 0)`);

        /* Create rectangles for each cell in the plate*/
        const d3Cells = cellGroups.append('rect').attr('class', 'well')
            .attr('width', `${cellSize.width}px`)
            .attr('height', `${cellSize.height}px`)
            .on('click', (pos) => {
                let injectionId = this._d3cells.get(pos).attr('data-injection-id');
                if (injectionId) {
                    const {altKey, shiftKey} = d3.event;
                    this.#callbacks.onCellClick.forEach(cb => cb({
                            plateId: this._plateMeta.id,
                            injectionId: injectionId,
                            altKey: altKey,
                            shiftKey: shiftKey,
                        }
                    ));
                }
            })
            .on('mouseenter', (pos) => this.#callbacks.onInjectionHover.forEach(cb => cb(this._d3cells.get(pos).attr('data-injection-id'), true)))
            .on('mouseout', (pos) => this.#callbacks.onInjectionHover.forEach(cb => cb(this._d3cells.get(pos).attr('data-injection-id'), false)));

        const cells = {
            all: () => {
                return d3Cells
            },
            /**
             * @param {OnPlatePosition} pos
             * @returns {d3.selection}
             */
            get: (pos) => {
                return d3.select(d3Cells._groups[pos.rowIndex][pos.colIndex])
            }
        }

        return {root, cellSize, cells};
    }

    /**
     * @param {boolean} isInjectionPage
     * @return {drag}
     */
    _onDrag(isInjectionPage) {
        let selection;
        return d3.drag()
            .on('start', (coords, i, group) => {
                const e = d3.event.sourceEvent;
                if (isInjectionPage && !e.shiftKey)
                    return

                selection = [];
                this._startPoint = d3.mouse(group[i].closest('svg'));
                this._updateSelection(selection, d3.event.sourceEvent);
            })
            .on('drag', (coords, i, group) => {
                const e = d3.event.sourceEvent;
                if (isInjectionPage && !e.shiftKey)
                    return

                selection = [];
                const end = d3.mouse(group[i].closest('svg'));

                const x0 = Math.max(0, Math.floor(Math.min(this._startPoint[0], end[0]) / (this._cellSize.width + 2)));
                const y0 = Math.max(0, Math.floor(Math.min(this._startPoint[1], end[1]) / (this._cellSize.height + 2)));
                const x1 = Math.min(this._plateMeta.cols, Math.ceil(Math.max(this._startPoint[0], end[0]) / (this._cellSize.width + 2)));
                const y1 = Math.min(this._plateMeta.rows, Math.ceil(Math.max(this._startPoint[1], end[1]) / (this._cellSize.height + 2)));

                for (let row = y0; row < y1; row++) {
                    for (let col = x0; col < x1; col++) {
                        let injectionId = this._d3cells.get(new OnPlatePosition(row, col)).attr('data-injection-id');
                        if (injectionId)
                            selection.push(injectionId)
                    }
                }

                this._updateSelection(selection, d3.event.sourceEvent);
            })
            .on('end', (coords, i, group) => {
                const e = d3.event.sourceEvent;
                if (isInjectionPage && !e.shiftKey)
                    return

                let mouse = d3.mouse(group[i].closest('svg'));
                if (this._startPoint[0] === mouse[0] && this._startPoint[1] === mouse[1] && this._d3cells.get(coords).attr('data-injection-id'))
                    return; // it's a click, not a drag. Check if clicked cell is not empty and skip if it's true
                const event = SelectionHelper.createChangeSelectionEvent(new Map(selection.map(i => [i, null])),
                    d3.event.sourceEvent);
                this.#callbacks.onChangeSelection.forEach(cb => cb(event));
            });
    }

    _setSelection(ids, isSelected) {
        for (const id of ids) {
            let pos = this._plate.getCoordinatesByInjectionId(id);
            let cell = this._d3cells.get(pos);
            if (cell)
                cell.classed('user-selection', isSelected)
        }
    }

    _selectExclusively(ids) {
        this._d3cells.all().classed('user-selection', false);
        for (const id of ids) {
            let pos = this._plate.getCoordinatesByInjectionId(id);
            let cell = this._d3cells.get(pos);
            if (cell)
                cell.classed('user-selection', true)
        }
    }

    _updateSelection(ids, keys) {
        if (keys.shiftKey)
            this._setSelection(ids, !keys.altKey);
        else
            this._selectExclusively(ids);
    }

    /**
     * @param {PlateMeta} plateMeta
     * @return {string}
     */
    static htmlTemplate(plateMeta) {
        return `<div class="plate-container" data-plate-id="${plateMeta.id}">
                    <div class="progress visually-hidden">
                        <div class="indeterminate"></div>
                    </div>
                    <div class="plate-body">
                        <svg class="plate"></svg>
                    </div>
                </div>`
    }
}